import type * as api from '@gitbook/api-client';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { apiClient } from '../../state';
import { useCreateInOwner } from '../ContentListing';

export interface OrganizationImportTarget {
    organization: string;
}

export interface SiteImportTarget extends OrganizationImportTarget {
    site: api.Site;
}

export interface SpaceImportTarget {
    space: api.Space;
    changeRequest: string | undefined;
    page: string | undefined;
}

export type ImportTarget = SiteImportTarget | OrganizationImportTarget | SpaceImportTarget;

/**
 * Returns true if the target of the import is an organization.
 */
export function isOrganizationImportTarget(
    target: ImportTarget
): target is OrganizationImportTarget {
    return 'organization' in target;
}

export interface ImportResolvedTarget {
    space: api.Space;
    site?: api.Site | undefined;
    changeRequestId?: string | undefined;
    pageId?: string | undefined;
}

interface Context {
    /**
     * Function to get or create the space to import into.
     * If null, it means the user does not have the right to do this operation.
     */
    resolveTarget: (() => Promise<ImportResolvedTarget>) | null;
}

function SpaceImportTargetContext(props: {
    target: SpaceImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const resolveTarget = useEventCallback(async () => ({
        space: props.target.space,
        changeRequestId: props.target.changeRequest,
        pageId: props.target.page,
    }));
    return props.children({ resolveTarget });
}

/**
 * Modal to use when importing into a new space, in an organization.
 */
function OrganizationImportTargetContext(props: {
    target: OrganizationImportTarget | SiteImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;
    const { createSpace } = useCreateInOwner({
        organization: target.organization,
    });
    const resolveTarget = useEventCallback(async () => {
        assert(createSpace, 'Permission denied should be caught earlier');
        const space = await createSpace({ withNavigate: false, source: 'import' });
        const { data: changeRequest } = await apiClient.changeRequests.createChangeRequest(
            space.id,
            { subject: 'Import content' }
        );
        return {
            site: 'site' in target ? target.site : undefined,
            space,
            changeRequestId: changeRequest.id,
        };
    });
    return props.children({ resolveTarget: createSpace ? resolveTarget : null });
}

export function ImportTargetContext(props: {
    target: SpaceImportTarget | OrganizationImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;

    if ('organization' in target) {
        return (
            <OrganizationImportTargetContext target={target}>
                {props.children}
            </OrganizationImportTargetContext>
        );
    }

    return <SpaceImportTargetContext target={target}>{props.children}</SpaceImportTargetContext>;
}
